import { type Holding } from 'src/shared/types/holding';
import { type Contact, type ContactSettings } from 'src/shared/types/contact';

export enum RepaymentDetailsFileFormat {
  PAYFLOW = 'payflow',
  A3INNUVA = 'a3innuva',
  A3NOM = 'a3nom',
  PRIMAVERA = 'primavera',
  SERESCO = 'seresco',
  SOFTLAND_QUERY7 = 'softland query7'
}

export type CompanyFileSettings = {
  code?: string;
  conceptCode?: string;
  incidenceCode?: string;
  fileType: RepaymentDetailsFileFormat;
};

export type CompanySettings = {
  id: string;
  maxWithdrawalPercentage: number;
  maxEarnedWithdrawalPercentage: number;
  maxWithdrawalAmount: number;
  maxTransactionsPerPeriod: number;
  cutoffDay: number[];
  payoutDay: number[];
  paymentsThruApi: boolean;
  appEnabled: boolean;
  paymentFrequency: string;
  cycleStartDates: number[];
  cycleEndDates: number[];
  mfaEnabled: boolean;
  extraPayment: boolean;
  createdAt: string;
  updatedAt: string;
  fileSettings: CompanyFileSettings;
};

export type Company = {
  id: string;
  name: string;
  legalName: string;
  taxIdentifier: string;
  billingAddress: string;
  serviceStartedAt: string;
  settings: CompanySettings;
  metadata?: {
    integration?: {
      externalId: string;
    };
    billing?: {
      accountId: string;
      directDebitMandateId: string | null;
      selfFundingDirectDebitIban?: string | null;
    };
    legacyCore?: {
      id: number;
      taxId: string;
      companyId: number;
    };
  };
  createdAt: string;
  updatedAt: string;
  holding: Holding;
};

// it's for sending to backend POST and PUT
export type CompanyPayload = {
  name?: string;
  legalName?: string;
  taxIdentifier?: string;
  billingAddress?: string;
  selfFundingDirectDebitIban?: string | null;
  holding?: {
    id: string;
  };
  metadata?: {
    integration?: {
      externalId?: string;
    };
    billing?: {
      directDebitMandateId?: string;
    };
  };
  settings?: {
    id?: string;
    fileSettings?: {
      code?: string;
      conceptCode?: string;
      incidenceCode?: string;
      fileType?: RepaymentDetailsFileFormat;
    };
  };
};

export type CompanyContactSettings = ContactSettings & {
  contact: Contact;
};

export type CompanyContactSettingsPayload = ContactSettings & {
  contact: {
    id: string;
  };
};

export type SetSettingsByCompanyContactPayload = {
  companyId: string;
  data: CompanyContactSettingsPayload[];
};

export enum ProvidersList {
  UNNAX = 'unnax',
  RAILSBANK = 'railsbank',
  DEVENGO = 'devengo',
  DLOCAL_CO = 'dlocal_co',
  DLOCAL_PE = 'dlocal_pe',
  BANCOLOMBIA = 'bancolombia',
  DAVIVIENDA = 'davivienda',
  PAYFLOW = 'payflow',
  SWAN = 'swan'
}

export enum AccountStatus {
  CREATED = 'created',
  ACTIVATED = 'activated',
  CANCELLED = 'cancelled'
}

export type SelfFundedAccountTopUp = {
  date: string;
  amount: number;
};

export type SelfFundedAccount = {
  lowOnFundsThreshold: number;
  provider: ProvidersList;
  accountId: string;
  bank: string;
  accountNumber: string;
  status: AccountStatus;
  balance: number | string;
  currency: string;
  taxIdentifier: string;
};

export type SelfFundedAccountFullData = {
  account: SelfFundedAccount;
  topUps: SelfFundedAccountTopUp[];
};
